import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { Link, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { useGetServiceProviderByIdQuery } from '@/services/ServiceProviderService';
import { ServiceProviderDetailsTab } from './components/ServiceProviderDetailsTab';
import { ServiceTab } from './components/ServiceTab';
import { KeenIcon } from '@/components';
import ServiceBookingManagement from './ServiceBookingManagement';
import ServiceProviderAvailabilityCalendar from './components/ServiceProviderAvailabilityCalendar';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDemo1Layout, Demo1LayoutContext } from '@/layouts/demo1';
import { selectServiceProviderCapabilities } from '@/store/serviceProviderCapabilitiesSlice';
import { ServiceProviderCapability } from '@/constant/ServiceProvider';

// Tab components
const AvailabilityManagement = () => <ServiceProviderAvailabilityCalendar />;
const BookingManagement = () => {
  const selectedServiceProviderId = useSelector(
    (state: RootState) => state.auth.selectedServiceProviderId
  );
  return (
    <ServiceBookingManagement
      serviceProviderId={selectedServiceProviderId || undefined}
    />
  );
};

export default function ServiceProviderDetailsView() {
  const location = useLocation();
  const { layout } = useDemo1Layout() as unknown as Demo1LayoutContext;
  const { hasPermission } = usePermissions();

  const selectedServiceProviderId = useSelector(
    (state: RootState) => state.auth.selectedServiceProviderId
  );

  const { data: serviceProviderData } = useGetServiceProviderByIdQuery(
    selectedServiceProviderId || '',
    {
      skip: !selectedServiceProviderId,
    }
  );

  const hasServicePermission = hasPermission('service_management_read');
  const hasProviderPermission = hasPermission(
    'service_provider_management_read'
  );

  // Get service provider capabilities from Redux
  const serviceProviderCapabilities = useSelector(
    selectServiceProviderCapabilities
  );

  // Check if BOOKINGS_MANAGE_BOOKABLE_SERVICES capability is present
  const hasManageBookableServicesCapability = useMemo(() => {
    if (
      !serviceProviderCapabilities ||
      serviceProviderCapabilities.length === 0
    ) {
      return false;
    }
    return serviceProviderCapabilities.includes(
      ServiceProviderCapability.BOOKINGS_MANAGE_BOOKABLE_SERVICES
    );
  }, [serviceProviderCapabilities]);

  // Check if BOOKINGS_ACCEPT_ONLINE_BOOKINGS capability is present
  const hasAcceptOnlineBookingsCapability = useMemo(() => {
    if (
      !serviceProviderCapabilities ||
      serviceProviderCapabilities.length === 0
    ) {
      return false;
    }
    return serviceProviderCapabilities.includes(
      ServiceProviderCapability.BOOKINGS_ACCEPT_ONLINE_BOOKINGS
    );
  }, [serviceProviderCapabilities]);

  // Define navigation items based on permissions and capabilities
  const navItems = useMemo(() => {
    const items = [];
    if (hasProviderPermission) {
      items.push({
        id: 'service-provider-details',
        name: 'Service Provider Details',
        href: '#',
      });
    }
    // Add Service Management tab only if permission AND BOOKINGS_MANAGE_BOOKABLE_SERVICES capability are present
    if (hasServicePermission && hasManageBookableServicesCapability) {
      items.push({
        id: 'service-management',
        name: 'Service Management',
        href: '#',
      });
    }
    // Add Availability Management tab
    items.push({
      id: 'availability-management',
      name: 'Availability Management',
      href: '#',
    });
    // Add Booking Management tab only if BOOKINGS_ACCEPT_ONLINE_BOOKINGS capability is present
    if (hasAcceptOnlineBookingsCapability) {
      items.push({
        id: 'booking-management',
        name: 'Booking Management',
        href: '#',
      });
    }
    return items;
  }, [
    hasServicePermission,
    hasProviderPermission,
    hasManageBookableServicesCapability,
    hasAcceptOnlineBookingsCapability,
  ]);

  const urlSearchParams = new URLSearchParams(location.search);
  const tabFromUrl = urlSearchParams.get('tab');

  const [activeTab, setActiveTab] = useState(
    tabFromUrl && navItems.some((item) => item.id === tabFromUrl)
      ? tabFromUrl
      : navItems[0]?.id || 'service-provider-details'
  );

  // Update URL when tab changes
  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    if (currentParams.get('tab') !== activeTab) {
      currentParams.set('tab', activeTab);
      window.history.replaceState(
        null,
        '',
        `${location.pathname}?${currentParams.toString()}`
      );
    }
  }, [activeTab, location.pathname, location.search]);

  // Mobile pager: cycle through tabs one at a time
  const currentIndex = navItems.findIndex((item) => item.id === activeTab);
  const goPrevTab = () => {
    const prevIndex = (currentIndex - 1 + navItems.length) % navItems.length;
    const id = navItems[prevIndex].id;
    setActiveTab(id);
    window.history.pushState(null, '', `?tab=${id}`);
  };
  const goNextTab = () => {
    const nextIndex = (currentIndex + 1) % navItems.length;
    const id = navItems[nextIndex].id;
    setActiveTab(id);
    window.history.pushState(null, '', `?tab=${id}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'service-provider-details':
        return serviceProviderData?.data ? (
          <ServiceProviderDetailsTab
            serviceProvider={serviceProviderData.data}
          />
        ) : null;
      case 'service-management':
        return <ServiceTab serviceProviderId={selectedServiceProviderId} />;
      case 'availability-management':
        return <AvailabilityManagement />;
      case 'booking-management':
        return <BookingManagement />;
      default:
        return serviceProviderData?.data ? (
          <ServiceProviderDetailsTab
            serviceProvider={serviceProviderData.data}
          />
        ) : null;
    }
  };

  return (
    <div className="service-provider-page w-full">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-stretch sm:items-center flex-col sm:flex-row gap-1 sm:gap-4">
          <div className="mt-3">
            <Link
              to="/service-management"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
              aria-label="Back to service management"
            >
              <KeenIcon
                icon="black-left"
                className="text-xl"
                aria-hidden="true"
              />
            </Link>
          </div>
          <header
            className={`flex-1 md:border-b border-gray-200 service-provider-header ${layout.options.sidebar.collapse ? 'header-collapsed' : ''} overflow-hidden`}
          >
            <nav
              className="-mb-px flex space-x-6 overflow-x-auto px-1"
              aria-label="Tabs"
              role="tablist"
              style={{ maxWidth: 'calc(100vw - 200px)' }}
            >
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={`?tab=${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(item.id);
                    // Update URL without navigation
                    window.history.pushState(null, '', `?tab=${item.id}`);
                  }}
                  role="tab"
                  aria-selected={activeTab === item.id}
                  aria-controls={`service-provider-tabpanel-${item.id}`}
                  className={`whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm flex items-center gap-1 flex-shrink-0 min-w-fit contrast:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-sm ${
                    activeTab === item.id
                      ? 'border-blue-500 text-blue-600 contrast:text-blue-500 contrast:border-blue-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            {/* Mobile-only pager: hidden by default, shown at 320px via CSS */}
            <div
              className="service-provider-mobile-nav hidden items-center justify-between"
              aria-label="Tabs mobile"
            >
              <button
                type="button"
                className="nav-arrow prev"
                onClick={goPrevTab}
                aria-label="Previous section"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="current-tab" aria-current="page">
                {navItems[currentIndex]?.name}
              </div>
              <button
                type="button"
                className="nav-arrow next"
                onClick={goNextTab}
                aria-label="Next section"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </header>
        </div>
        <div
          className="py-8"
          role="tabpanel"
          id={`service-provider-tabpanel-${activeTab}`}
          aria-label={`${navItems[currentIndex]?.name || 'Section'} content`}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export { ServiceProviderDetailsView };
